// GeoIP types
interface GeoIPCountry {
  iso_code: string
  names?: { en: string }
}

interface GeoIPSubdivision {
  iso_code: string
  names?: { en: string }
}

interface GeoIPLocation {
  accuracy_radius?: number
  latitude?: number
  longitude?: number
  time_zone?: string
}

interface GeoIPTraits {
  ip_address?: string
}

export interface GeoIPCityResponse {
  city?: { names?: { en: string } }
  country: GeoIPCountry
  registered_country?: GeoIPCountry
  location?: GeoIPLocation
  subdivisions?: GeoIPSubdivision[]
  traits?: GeoIPTraits
}

// window extension
/**
 * Interrogates the MaxMind GeoIP API to get the country and region of the user.
 * If the request fails, it returns unknown values.
 * @returns {Promise<{ country: string; region: string }>}
 */
export async function getCountryAndRegion(): Promise<{ country: string | null; region: string | null }> {
  const defaultCountryAndRegion = {
    country: null,
    region: null
  }

  try {
    await loadGeoIPScript()
  } catch (error) {
    console.error('Error loading geoip2 script:', error)
    return defaultCountryAndRegion
  }

  return new Promise((resolve) => {
    if (!window.geoip2) {
      console.error('geoip2 library not loaded')
      return resolve(defaultCountryAndRegion)
    }

    window.geoip2.city(
      (data) => {
        const country = data.country?.names?.en?.toLowerCase() || null
        const region = data.subdivisions?.[0]?.names?.en?.toLowerCase() || null
        resolve({ country, region })
      },
      (error: any) => {
        console.error('Error fetching geo data:', error)
        resolve(defaultCountryAndRegion)
      }
    )
  })
}

/**
 * Interrogates the MaxMind GeoIP API to get the user's IPv6 address.
 * If the user not has an IPv6 address, it returns IPv4.
 * If the request fails, it returns null.
 * @returns {Promise<string | null>} The user's IPv6 address or IPv4 address, or null if the request fails.
 */
export async function getIPv6Address(): Promise<string | null> {
  try {
    await loadGeoIPScript()
  } catch (error) {
    console.error('Error loading geoip2 script:', error)
  }

  return new Promise((resolve) => {
    if (!window.geoip2) {
      console.error('geoip2 library not loaded')
      return resolve(null)
    }

    window.geoip2.city(
      (data) => {
        const ip = data?.traits?.ip_address || null
        resolve(ip)
      },
      (error: any) => {
        console.error('Error fetching geo data:', error)
        resolve(null)
      }
    )
  })
}

/**
 * Loads the MaxMind GeoIP script.
 * @returns {Promise<void>}
 */
function loadGeoIPScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.geoip2) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://geoip-js.com/js/apis/geoip2/v2.1/geoip2.js'
    script.type = 'text/javascript'
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => reject()
    document.head.appendChild(script)
  })
}
